/* eslint-disable react/prop-types */
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, {useCallback} from 'react';
import {useDropzone} from 'react-dropzone';
import getConfig from '../../../config';
import GlobalsUtils from '../../../utils/Globals';
import './NewFileDropCard.scss';
import {FormattedMessage} from 'react-intl';
import ModalContainer from '@react/react-spectrum/ModalContainer';
import Dialog from '@react/react-spectrum/Dialog';

interface NewFileDropCardProps {
	createNewFileLink: string;
	iconImgSrc?: string;
	cardTitle?: string;
	i?: number;
	intl: any;
	appContextType?: string; // optionally, for the future when we need to differentiate Dropbox cards
}

const NewFileDropCard: React.FC<NewFileDropCardProps> = ({
	createNewFileLink,
	iconImgSrc = '/assets/S_Illu_PsWeb_NewFile_160x120.svg',
	cardTitle = 'New File',
	i = 0,
	intl,
	appContextType,
}) => {
	const onDrop = useCallback((acceptedFiles) => {
		// For the files dragged/imported into the dropzone
		// set to proxy frame the file, then redirect to PS Web
		const photoshopWebUrl = getConfig('PHOTOSHOP_WEB');
		const response = GlobalsUtils.setToProxyFrame(photoshopWebUrl, acceptedFiles);
		if (response instanceof Error) {
			// TODO: Get real strings from Katie on this error modal
			const errorTitle = intl.formatMessage({id: 'appHomes.error.modal.psweb.title'});
			const errorDesc = intl.formatMessage({id: 'apphomes.error.modal.psweb.description'}, {filename: ''});
			const errorModal = (
				<Dialog confirmLabel='OK' open role='dialog' title={errorTitle} trapFocus variant='error'>
					<span>{errorDesc}</span>
				</Dialog>
			);
			ModalContainer.show(errorModal, this);
		}
	}, []);

	// TODO: Implement fileRejections and open a modal if an invalid file
	const {getRootProps, getInputProps, open, isDragActive} = useDropzone({
		onDrop,
		noClick: true,
		maxFiles: 1,
		// https://github.com/alexschiller/file-format-commons
		accept: 'image/png, image/tiff, image/jpeg, .jpg, image/heic, .psd, .psb, .heic',
		onDropRejected: (fileRejections) => {

			let errorTitle;
			let errorDesc;
			if (fileRejections.length > 1) {
				errorTitle = intl.formatMessage({id: 'appHomes.error.modal.psweb.multiplefiles.title'});
				errorDesc = intl.formatMessage({id: 'appHomes.error.modal.psweb.multiplefiles.description'});
			}
			else {
				const fileRejection = fileRejections[0];
				const file = fileRejection.file;
				errorTitle = intl.formatMessage({id: 'appHomes.error.modal.psweb.title'});
				errorDesc = intl.formatMessage(
					{id: 'apphomes.error.modal.psweb.description'},
					{filename: file.name || ''},
				);
			}

			const errorModal = (
				<Dialog confirmLabel='OK' open role='dialog' title={errorTitle} trapFocus variant='error' className='new-file-drag-drop-error-modal'>
					<span>{errorDesc}</span>
				</Dialog>
			);
			ModalContainer.show(errorModal, this);
		},
	});

	const handleSelectFileClick: React.MouseEventHandler<HTMLSpanElement> = (e) => {
		e.preventDefault();
		e.stopPropagation();
		open();
	};

	const handleCardClick = (e) => {
		e.preventDefault();
		window.location.href = createNewFileLink;
	};

	if (appContextType && appContextType === 'illustratorweb') {
		return (
			<button
				key={`dropbox-${i}`}
				className={`dropbox-card${isDragActive ? ' drag-active' : ''}`}
				onClick={handleCardClick}
				role='button'
				aria-label='Select a file to import'
			>
				<img src={iconImgSrc} alt={cardTitle} className={isDragActive ? 'drag-active' : ''} />
				<h3>{cardTitle}</h3>
			</button>
		);
	}

	return (
		<button
			key={`dropbox-${i}`}
			className={`dropbox-card${isDragActive ? ' drag-active' : ''}`}
			{...getRootProps()}
			onClick={handleCardClick}
			role='button'
			aria-label='Select a file to import'
		>
			<input {...getInputProps()} />
			<img src={iconImgSrc} alt={cardTitle} className={isDragActive ? 'drag-active' : ''} />
			<h3>{cardTitle}</h3>
			<p>
				<FormattedMessage id='appHomes.cards.newFile.or' />{' '}
				<span onClick={handleSelectFileClick}>
					<FormattedMessage id='appHomes.cards.newFile.select' />
				</span>{' '}
				<FormattedMessage id='appHomes.cards.newFile.import' />
			</p>
		</button>
	);
};
export default NewFileDropCard;
export type {NewFileDropCardProps};
